import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { companyInfo } from '../mock';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Store in localStorage (mock backend)
    const contactData = {
      ...formData,
      submittedAt: new Date().toISOString(),
      id: Date.now().toString()
    };

    const existingContacts = JSON.parse(localStorage.getItem('contacts') || '[]');
    localStorage.setItem('contacts', JSON.stringify([...existingContacts, contactData]));

    setIsSubmitted(true);
    toast({
      title: 'Message Sent Successfully',
      description: 'We\'ll get back to you as soon as possible.'
    });
  };

  if (isSubmitted) {
    return (
      <div className="bg-gray-950 text-white min-h-screen flex items-center justify-center py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-20 h-20 bg-green-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-500" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Thank You!</h1>
          <p className="text-xl text-gray-300 mb-8">
            Your message has been sent successfully. We'll get back to you as soon as possible.
          </p>
          <Button
            onClick={() => {
              setIsSubmitted(false);
              setFormData({ name: '', email: '', phone: '', company: '', message: '' });
            }}
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-md font-semibold">

            Send Another Message
          </Button>
        </div>
      </div>);

  }

  return (
    <div className="bg-gray-950 text-white min-h-screen py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
          <p className="text-xl text-gray-400">
            Get in touch with our team to discuss your precision machining needs
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="bg-gray-900 border-gray-800 p-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="h-6 w-6 text-red-500" />
                </div>
                <div>
                  <h3 className="!font-semibold !text-lg mb-2 !text-[#FFFFFF] !bg-[rgba(255,255,255,0)]">Phone</h3>
                  <a href={`tel:${companyInfo.phone}`} className="text-gray-400 hover:text-red-500 transition-colors duration-200">
                    {companyInfo.phone}
                  </a>
                </div>
              </div>
            </Card>

            <Card className="bg-gray-900 border-gray-800 p-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="h-6 w-6 text-red-500" />
                </div>
                <div>
                  <h3 className="!font-semibold !text-lg mb-2 !text-[#FFFFFF]">Email</h3>
                  <a href={`mailto:${companyInfo.email}`} className="text-gray-400 hover:text-red-500 transition-colors duration-200">
                    {companyInfo.email}
                  </a>
                </div>
              </div>
            </Card>

            <Card className="bg-gray-900 border-gray-800 p-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-6 w-6 text-red-500" />
                </div>
                <div>
                  <h3 className="!font-semibold !text-lg mb-2 !text-[#FFFFFF]">Address</h3>
                  <p className="text-gray-400">
                    {companyInfo.address}<br />
                    {companyInfo.city}<br />
                    {companyInfo.country}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="bg-gray-900 border-gray-800 p-6">
              <h3 className="!font-semibold !text-lg mb-4 !text-[#FFFFFF]">Business Hours</h3>
              <div className="space-y-2 text-gray-400">
                <div className="flex justify-between">
                  <span>Monday - Friday:</span>
                  <span className="text-white">8:00 AM - 5:00 PM</span>
                </div>
                <div className="flex justify-between">
                  <span>Saturday:</span>
                  <span className="text-white">By Appointment</span>
                </div>
                <div className="flex justify-between">
                  <span>Sunday:</span>
                  <span className="text-white">Closed</span>
                </div>
              </div>
            </Card>

            <Card className="bg-gray-900 border-gray-800 p-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Instagram className="h-6 w-6 text-red-500" />
                </div>
                <div>
                  <h3 className="!font-semibold !text-lg mb-2 !text-[#FFFFFF]">Follow Us</h3>
                  <a 
                    href="https://www.instagram.com/aethermachining" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-red-500 transition-colors duration-200"
                  >
                    @aethermachining
                  </a>
                </div>
              </div>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="bg-gray-900 border-gray-800 p-8">
              <h2 className="!font-bold !text-3xl mb-6 !text-[#FFFFFF]">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-white">Your Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-gray-800 border-gray-700 text-white mt-2" />

                  </div>
                  <div>
                    <Label htmlFor="email" className="text-white">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-gray-800 border-gray-700 text-white mt-2" />

                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-white">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="bg-gray-800 border-gray-700 text-white mt-2" />

                  </div>
                  <div>
                    <Label htmlFor="company" className="text-white">Company Name</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="bg-gray-800 border-gray-700 text-white mt-2" />

                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-white">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={8}
                    className="bg-gray-800 border-gray-700 text-white mt-2"
                    placeholder="Tell us about your project or ask us a question..." />

                </div>

                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white py-4 text-lg font-semibold rounded-md transition-all duration-300 flex items-center justify-center space-x-2">
                  <Send className="h-5 w-5" />
                  <span>Send Message</span>
                </Button>
              </form>
            </Card>
          </div>
        </div>
      </div>
    </div>);

};

export default Contact;